import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, NgModule, provideAppInitializer} from '@angular/core';
import {MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import {MatDialogModule} from '@angular/material/dialog';
import {BrowserModule} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {RouteReuseStrategy, RouterModule} from '@angular/router';
import {BREAKPOINT, BreakPoint} from '@ngbracket/ngx-layout';
import {provideTranslateService, TranslateCompiler, TranslatePipe} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {Configuration} from '@npck/coid-frontend-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {
    AppConfigService,
    AppRouteReuseStrategy,
    BusinessErrorCode,
    CommonError,
    customBreakPoints,
    SentryInterceptor,
    SentryService,
    TracingInterceptor,
    TracingService,
} from '@npck/libs/commons';
import {ERROR_COMPONENT_CONFIG, ErrorComponentConfig} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/kk';
import 'dayjs/locale/ru';
import customParserFormat from 'dayjs/plugin/customParseFormat';
import {HIGHLIGHT_OPTIONS} from 'ngx-highlightjs';
import {provideNgxMask} from 'ngx-mask';
import {TranslateMessageFormatCompiler} from 'ngx-translate-messageformat-compiler';
import {provideNgxWebstorage, withLocalStorage, withSessionStorage} from 'ngx-webstorage';
import versionConfig from '../assets/config/version.json';
import {appRoutes} from './app-routing.module';
import {AuthHolderService} from './domain/auth-holder.service';
import {AppErrorHandler, Error} from './infra/app-error-handler.service';
import {AppComponent} from './root/app.component';

dayjs.extend(customParserFormat);
dayjs.locale('ru');
registerLocaleData(localeRu, 'ru');

const DESKTOP_TO_MOBILE_BREAKPOINT = 1028;

const ERROR_CONFIG: ErrorComponentConfig = {
    errorIcons: {
        [CommonError.PAGE_NOT_FOUND]: 'ic_404',
        [CommonError.SERVER_ERROR]: 'ic_500',
        [CommonError.NETWORK_ERROR]: 'ic_error_no_network',
        [Error.MODERATOR_FORBIDDEN]: 'ic_warning',
    },
    errorsWithWhiteBackground: [
        CommonError.SERVER_ERROR,
        CommonError.NETWORK_ERROR,
        Error.MODERATOR_FORBIDDEN,
    ],
    errorsWithRequestId: [
        CommonError.PAGE_NOT_FOUND,
        CommonError.SERVER_ERROR,
        CommonError.NETWORK_ERROR,
        Error.MODERATOR_FORBIDDEN,
    ],
};

const apiConfigurationFactory = (authHolderService: AuthHolderService): Configuration => new Configuration({
    basePath: '',
    credentials: {
        AuthToken: () => authHolderService.authToken,
        RevokeESignAuth: () => authHolderService.revokeToken,
    },
});

export function breakPointsProviderFactory(): BreakPoint[] {
    return customBreakPoints(DESKTOP_TO_MOBILE_BREAKPOINT);
}

export function initApp(
    analyticsService: AnalyticsService,
    appConfigService: AppConfigService,
    authHolderService: AuthHolderService,
    tracingService: TracingService,
    sentryService: SentryService,
): () => Promise<any> {
    return () => appConfigService.init({...versionConfig, enableBackendConfig: true})
        .then(() => tracingService.init(span => {
            span.setAttribute('userId', authHolderService.userData?.userId);
            span.setAttribute('role', authHolderService.userData?.role);
            span.setAttribute('iin', authHolderService.userData?.iinEncrypted);
            span.setAttribute('clientTime', new Date().toString());
        }))
        .then(() => analyticsService.init(
            'COID',
            () => ({
                role: authHolderService.userData?.role,
                traceId: tracingService.getTraceId(),
            }),
            authHolderService.userData?.userId?.toString()),
        )
        .then(() => sentryService.init({
            tagsFactory: () => ({
                'userId': authHolderService.userData?.userId,
                'role': authHolderService.userData?.role,
                'iin': authHolderService.userData?.iinEncrypted,
                'openTelemetryTraceId': tracingService.getTraceId(),
                'clientTime': new Date().toString(),
            }),
            mutedEventsByTags: [
                [{'errorCode': BusinessErrorCode.DOCUMENT_NOT_FOUND}],
                [{'errorCode': BusinessErrorCode.IIN_ALREADY_EXISTS}, {'httpStatus': '409'}],
            ],
            groupsByTags: {
                'network': [
                    [{'httpStatusText': 'Unknown Error'}, {'httpStatus': '0'}],
                ],
            },
        }));
}

export const httpInterceptorProviders = [
    {provide: HTTP_INTERCEPTORS, useClass: TracingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SentryInterceptor, multi: true},
];

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        RouterModule.forRoot(appRoutes),
        MatNativeDateModule,
        MatDialogModule,
        TranslatePipe,
    ],
    providers: [
        AppRouteReuseStrategy,
        {provide: RouteReuseStrategy, useExisting: AppRouteReuseStrategy},
        provideAnimations(),
        provideAppInitializer(() => {
            return (initApp)(inject(AnalyticsService),
                inject(AppConfigService),
                inject(AuthHolderService),
                inject(TracingService),
                inject(SentryService))();
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideNgxWebstorage(
            withLocalStorage(),
            withSessionStorage(),
        ),
        provideNgxMask(),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json',
            }),
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler,
            },
        }),
        {provide: BREAKPOINT, useFactory: breakPointsProviderFactory, multi: true},
        {provide: ERROR_COMPONENT_CONFIG, useValue: ERROR_CONFIG},
        {provide: ErrorHandler, useClass: AppErrorHandler},
        {provide: Configuration, useFactory: apiConfigurationFactory, deps: [AuthHolderService]},
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                languages: {
                    json: () => import('highlight.js/lib/languages/json'),
                },
            },
        },
        {provide: MAT_DATE_LOCALE, useValue: 'ru'},
        httpInterceptorProviders,
    ],
})
export class AppModule {
}
